import _ from 'lodash'
import type { ShapeDividersCSSVars, MediaContainerWithDividersDesign } from '@wix/thunderbolt-becky-types'
import { CompInfo } from '@wix/thunderbolt-components-native'
import { ParsedSvg, color as colorsAPI } from '@wix/thunderbolt-commons'
import {
	getShapeDividerArgs,
	getShapeDividerImageContent,
	getLayersEffectProp,
	getUnitLengthPercentage,
	CompDesign,
	isMediaContainerWithDividers,
} from './ShapeDividers.utils'

const SHAPE_DIVIDER_MOBILE_HEIGHT = '50px'

const getShapeDividerImage = (
	compId: string,
	data: MediaContainerWithDividersDesign['dividerTop'],
	side: string,
	color: string,
	height: string,
	parsedSvg: ParsedSvg
) => {
	const shapeDividerArgs = getShapeDividerArgs({ compId, side, data })
	return getShapeDividerImageContent({ color, height, parsedSvg, ...shapeDividerArgs })
}

const getLayersDisplay = (side: string, layersEffects: any) => {
	return [1, 2, 3].reduce((acc, layerNumber) => {
		const display = layerNumber <= (layersEffects.size || 0) ? 'block' : 'none'
		acc[`--divider-${side}-layer-${layerNumber}-display`] = display
		return acc
	}, {} as any)
}

const getMobileVars = (mobile: CompDesign, side: 'top' | 'bottom') => {
	const dividerSide = side === 'top' ? 'dividerTop' : 'dividerBottom'
	const mobileDivider = mobile && isMediaContainerWithDividers(mobile) ? mobile[dividerSide] : null
	const disableMobile = !!(mobileDivider && mobileDivider?.svgId === 'none')
	return {
		divider: disableMobile ? null : mobileDivider,
		noMobile: !disableMobile && !mobileDivider,
		disableMobile,
	}
}

const getShapeDividersData = (
	compDesignPerVariantMap: {
		[variantId: string]: CompDesign | undefined
	},
	isMobileView: boolean,
	hasResponsiveLayout: boolean
) => {
	/*
	 * Get mobile dividers data - only if we're in mobile view
	 */
	const mobile =
		isMobileView && !hasResponsiveLayout
			? Object.entries(compDesignPerVariantMap).find(([key]) => key !== '')?.[1]
			: undefined
	const desktop = compDesignPerVariantMap['']
	let topDivider = desktop && isMediaContainerWithDividers(desktop) ? desktop.dividerTop : null
	let bottomDivider = desktop && isMediaContainerWithDividers(desktop) ? desktop.dividerBottom : null

	if (mobile && isMobileView) {
		const { divider: topMobile, noMobile: noMobileTop, disableMobile: disableMobileTop } = getMobileVars(
			mobile,
			'top'
		)
		const { divider: bottomMobile, noMobile: noMobileBottom, disableMobile: disableMobileBottom } = getMobileVars(
			mobile,
			'bottom'
		)

		topDivider = topMobile || topDivider
		bottomDivider = bottomMobile || bottomDivider

		return {
			topDivider,
			bottomDivider,
			noMobileTop,
			noMobileBottom,
			disableMobileTop,
			disableMobileBottom,
		}
	}

	return {
		topDivider,
		bottomDivider,
		noMobileTop: true,
		noMobileBottom: true,
		disableMobileTop: false,
		disableMobileBottom: false,
	}
}

const getDividerCSSVars = (
	compId: string,
	divider: MediaContainerWithDividersDesign['dividerTop'] | null,
	isMobileView: boolean,
	noMobile: boolean,
	side: string,
	colorsMap: Array<string> = [],
	disabled: boolean = false
) => {
	if (!divider) {
		return {}
	}

	if (disabled) {
		return {
			[`--divider-${side}-image`]: 'none',
			[`--divider-${side}-size`]: '',
			[`--divider-${side}-height`]: '',
			[`--divider-${side}-offset-x`]: '',
			[`--divider-${side}-color`]: '',
			[`--divider-${side}-opacity`]: '',
			[`--divider-${side}-flip`]: '',
			[`--divider-${side}-layers-size`]: '',
			[`--divider-${side}-layers-y`]: '0px',
			[`--divider-${side}-layers-x`]: '0px',
		}
	}

	const layersEffect = getLayersEffectProp(divider.layersEffect)
	const height = isMobileView && noMobile ? SHAPE_DIVIDER_MOBILE_HEIGHT : getUnitLengthPercentage(divider.height)
	const offsetX = getUnitLengthPercentage(divider.offsetX)
	const color = colorsAPI.getFromColorMap(divider.color, colorsMap)
	const opacity = divider.opacity ?? 1
	const padding = getUnitLengthPercentage(divider.padding)
	const xFlip = divider.flip ? '-1' : '1'
	const flip = `scale(${xFlip}, ${side === 'top' ? '-1' : '1'})`
	const layersPin = layersEffect?.pin

	const shouldFixMobileHairline = isMobileView
	const layerFilter = `drop-shadow(0px 1px 0px var(--divider-${side}-color, currentColor))`

	const dividerCssVars = {
		height,
		offsetX,
		color,
		opacity,
		image:
			// @ts-ignore TODO - should add 'resolvedSvg: ParsedSvg' to MediaContainerWithDividersDesign type in EE
			divider && divider.resolvedSvg
				? // @ts-ignore
				getShapeDividerImage(compId, divider, side, color, height, divider.resolvedSvg as ParsedSvg)
				: {},
		padding,
		flip,
		layersEffect,
		layersPin,
	}

	return {
		[`--divider-${side}-image`]: dividerCssVars.image?.image || 'none',
		[`--divider-${side}-size`]: dividerCssVars.image?.size || '',
		[`--divider-${side}-height`]: dividerCssVars.height,
		[`--divider-${side}-offset-x`]: dividerCssVars.offsetX,
		[`--divider-${side}-padding`]: dividerCssVars.padding,
		[`--divider-${side}-color`]: dividerCssVars.color,
		[`--divider-${side}-opacity`]: dividerCssVars.opacity,
		[`--divider-${side}-flip`]: dividerCssVars.flip,
		[`--divider-${side}-layers-size`]: dividerCssVars.layersEffect?.size,
		...(dividerCssVars.layersEffect?.y && { [`--divider-${side}-layers-y`]: dividerCssVars.layersEffect?.y }),
		...(dividerCssVars.layersEffect?.x && { [`--divider-${side}-layers-x`]: dividerCssVars.layersEffect?.x }),
		[`--divider-${side}-layers-pin-factor`]: !dividerCssVars.layersPin
			? 0
			: dividerCssVars.layersPin === 'out'
				? 1
				: -1,
		[`--divider-${side}-filter`]: shouldFixMobileHairline ? layerFilter : 'none',
		...getLayersDisplay(side, layersEffect),
	}
}

export const getShapeDividersCSSVars = ({
	compId,
	hasResponsiveLayout,
	isMobileView,
	compDesignPerVariantMap,
	colorsMap,
}: Pick<CompInfo, 'hasResponsiveLayout' | 'isMobileView' | 'compId'> & {
	compDesignPerVariantMap: Record<string, CompDesign | undefined>
	colorsMap: Array<string>
}): ShapeDividersCSSVars => {
	const {
		topDivider,
		bottomDivider,
		noMobileTop,
		noMobileBottom,
		disableMobileTop,
		disableMobileBottom,
	} = getShapeDividersData(compDesignPerVariantMap, isMobileView, hasResponsiveLayout)

	const topCssVars = getDividerCSSVars(
		compId,
		topDivider,
		isMobileView,
		noMobileTop,
		'top',
		colorsMap,
		disableMobileTop
	)
	const bottomCssVars = getDividerCSSVars(
		compId,
		bottomDivider,
		isMobileView,
		noMobileBottom,
		'bottom',
		colorsMap,
		disableMobileBottom
	)

	return { ...topCssVars, ...bottomCssVars }
}

export const getShapeDividersResponsiveCSSVars = ({
	compId,
	compDesign,
	colorsMap,
}: Pick<CompInfo, 'compId' | 'compDesign'> & {
	colorsMap: Array<string>
}) => {
	const topDivider = compDesign && (compDesign as MediaContainerWithDividersDesign).dividerTop
	const bottomDivider = compDesign && (compDesign as MediaContainerWithDividersDesign).dividerBottom

	const disableTop = topDivider && topDivider.svgId === 'none'
	const disableBottom = bottomDivider && bottomDivider.svgId === 'none'

	const topCssVars = getDividerCSSVars(compId, topDivider, false, false, 'top', colorsMap, disableTop)
	const bottomCssVars = getDividerCSSVars(compId, bottomDivider, false, false, 'bottom', colorsMap, disableBottom)

	return _.pickBy(
		{ ...topCssVars, ...bottomCssVars },
		(v) => _.isNumber(v) || v === '' || v
	) as ShapeDividersCSSVars & { '--divider-top-filter'?: string; '--divider-bottom-filter'?: string }
}
